import { createContext, useCallback, useContext, useState, type ReactNode } from "react";
import { Icon, type IconName } from "./Icon.js";

/**
 * Short confirmations that come and go: "Saved.", "Copied.". They replace the
 * green notices that used to appear under a button and shove the page down.
 *
 * The live region is always in the document, empty or not: a screen reader
 * only announces changes to a region that already existed when they happened.
 * Errors do not come here. An error belongs beside the thing that failed, and
 * must not vanish on a timer before somebody has read it.
 *
 * A toast can carry one action, usually Undo: a move made by a drag is one
 * slip of the wrist from the wrong bin, and asking first would make every
 * right one slower. It stays up longer, long enough to reach.
 *
 * The other is Reload, for a new build (lib/freshness.ts). That one is
 * `sticky`, up until it is used or closed: it is offered because somebody was
 * mid-task, and a timer would take it away before they finished.
 */

export interface ToastAction {
  label: string;
  run: () => void;
}

export interface ToastOptions {
  /** Stays until its action is used or it is closed, rather than timing out. */
  sticky?: boolean;
  icon?: IconName;
}

type Push = (message: string, action?: ToastAction, options?: ToastOptions) => void;

interface Shown {
  id: number;
  message: string;
  action?: ToastAction;
  options?: ToastOptions;
}

const ToastContext = createContext<Push>(() => undefined);

let next = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Shown[]>([]);

  const dismiss = useCallback(
    (id: number) => setToasts((current) => current.filter((toast) => toast.id !== id)),
    [],
  );

  const push = useCallback<Push>(
    (message, action, options) => {
      const id = (next += 1);
      setToasts((current) => [
        ...current.slice(-2),
        { id, message, ...(action ? { action } : {}), ...(options ? { options } : {}) },
      ]);
      if (!options?.sticky) window.setTimeout(() => dismiss(id), action ? 7000 : 3200);
    },
    [dismiss],
  );

  return (
    <ToastContext.Provider value={push}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {toasts.map((toast) => (
          <div key={toast.id} className={toast.action ? "toast toast-act" : "toast"}>
            <Icon name={toast.options?.icon ?? "check-circle"} size={18} />
            {toast.message}
            {toast.action && (
              <button
                type="button"
                className="toast-button"
                onClick={() => {
                  dismiss(toast.id);
                  toast.action?.run();
                }}
              >
                {toast.action.label}
              </button>
            )}
            {toast.options?.sticky && (
              <button
                type="button"
                className="toast-close"
                aria-label="Close"
                onClick={() => dismiss(toast.id)}
              >
                <Icon name="x" size={16} />
              </button>
            )}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export const useToast = (): Push => useContext(ToastContext);
